import { combineEpics, ofType } from "redux-observable";
import { catchError, EMPTY, endWith, from, map, mergeMap, of, startWith, switchMap, withLatestFrom } from "rxjs";
import { RootState } from "../../../store/ConfigureStore";
import { GetBasicSummary, GetBasicSummaryScenarios, getDepletionCurveData, GetDepletionPercentage, GetDepletionPressure, GetFutureBHP, GetFutureProductionRate, getIPRGraphData, getIPRTableData } from "../../../services/jetlift/jetlift-output/basicCals.Service";
import { setBasicCalculationComplete, setBasicIPRMethodologySelection, setBasicSummary, setbasicSummaryScenarios, setBasicWellInflowData, setBasicWellIPRDepletionPercentage, setBasicWellIPRDepletionPressure, setBasicWellIPRFutureBHP, setBasicWellIPRFutureProdRate, setCavitationWarning, setDepletionChartData, setFormattedBasicSummaryScenarios, setIPRChartData, setLoading } from "../../../store/jetlift/jetlift-output/BasicCalculationSlice";
import { DepletionPercentageInObject, FutureBHPInObject, IPR } from "../../../store/model/calculations/calculation";
import { basicSummaryPayload, basicSummaryScenariosPayload } from "../../../store/model/jetlift/jetlift";

const getWellInflowData = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/gasCalculations/setGasCalculationComplete"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.gasCalculations?.GasCalculationComplete)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([actionData, tubingInput, wellInput, fluidInput, gasCalculationComplete, unit]) => {
            if (gasCalculationComplete) {
                const inflowType = +(wellInput?.inflowTypeLstVal) || 0;
                let flowRate = null;
                let flowingBHHd = null;
                let flowingBHPressure = null;
                let piValue = null;
                let staticBHHd = null;
                let staticBHPressure = null;

                if (inflowType === 0) {
                    piValue = wellInput?.piVal?.toString() || "0";
                    staticBHPressure = wellInput?.reservoirPressure?.toString() || "0";
                    staticBHHd = wellInput?.staticFluidLvl?.toString() || "0";
                }
                if (inflowType === 1) {
                    flowRate = wellInput?.flowRate?.toString() || "0";
                    flowingBHPressure = wellInput?.intakePressure?.toString() || "0";
                    staticBHPressure = wellInput?.sbhpForPressre?.toString() || "0";
                }
                if (inflowType === 2) {
                    flowRate = wellInput?.flowRateForHd?.toString() || "0";
                    flowingBHHd = wellInput?.prdcngFluidLvl || 0;
                    staticBHHd = wellInput?.staticFluidLvlForHd?.toString() || "0";
                }

                const gasAvailability = fluidInput?.gasAvailblty;
                const isGasAvailable = gasAvailability && +gasAvailability ? true : false;
                const postObj = {
                    inflowType: +wellInput?.inflowTypeLstVal || 0,
                    staticBHPressure: +staticBHPressure,
                    staticBHPressureUnit: +unit?.unitReservoirPressureListVal,
                    pi: +piValue,
                    piUnit: +unit?.unitPIValListVal,
                    flowingBHPressure: +flowingBHPressure,
                    flowingBHPressureUnit: +unit?.unitIntakePressureListVal,
                    productionRate: +wellInput?.productionRate || 0,
                    productionRateUnit: +unit?.unitProductionRateListVal,
                    bubblePtPressure: +fluidInput?.bubblePoint || 0,
                    bubblePtPressureUnit: +unit?.unitBubblePointListVal,
                    flowRate: +flowRate,
                    flowRateUnit: +unit?.unitFlowRateListVal,
                    watercut: +fluidInput?.waterCut || 0,
                    staticBHHead: +staticBHHd,
                    staticBHHeadUnit: +unit?.unitStaticFluidLvlListVal,
                    flowingBHHead: +flowingBHHd,
                    flowingBHHeadUnit: +unit?.unitPrdcngFluidLvlListVal,
                    combinedSpGr: +fluidInput?.combinedSpGr || 0,
                    perforation: +wellInput?.perforation || 0,
                    perforationUnit: +unit?.unitPerforationListVal,
                    unitSystem: tubingInput?.unitSys,
                    isGas: isGasAvailable,
                }

                return from(getIPRTableData(postObj)).pipe(
                    map(data => {
                        var ipr = data?.iprSelected;
                        let selection = "";
                        if (ipr === 'Wiggins' || ipr === 'Vogel' || ipr === 'ConstantPI' || ipr === 'Composite')
                            selection = ipr;

                        var result = { data, selection };
                        return result
                    }),
                    mergeMap(result => {
                        return [
                            setBasicWellInflowData(result?.data?.wellInflowData),
                            setBasicIPRMethodologySelection(result?.selection),
                        ]
                    }),
                    catchError((error) => of(console.log(error))),
                    startWith(setLoading(true)),
                )
            }
            return EMPTY;
        }),
    )
};

const getWellIPRData = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/basicCalculation/setBasicWellIPRDepletionPressure"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.depletionPressure))
        ),
        switchMap(([actionData, tubingInput, wellInput, fluidInput, depletionPressure]) => {
            let depletionPercentageObj = {} as DepletionPercentageInObject;
            depletionPercentageObj.inflowType = +(wellInput?.inflowTypeLstVal || 0);
            depletionPercentageObj.staticBHP = wellInput?.sbhpForPressre === null ? 0 : wellInput?.sbhpForPressre;
            depletionPercentageObj.staticFluidLevel = +wellInput?.staticFluidLvl || 0;
            depletionPercentageObj.compositeSpGr = +fluidInput?.combinedSpGr || 0;
            depletionPercentageObj.depletionPressure = depletionPressure || 0;
            depletionPercentageObj.datum = +wellInput?.perforation || 0;//
            depletionPercentageObj.casingPressure = +wellInput?.casingPressure || 0;

            if (depletionPercentageObj.inflowType === 0) {
                depletionPercentageObj.staticBHP = wellInput?.reservoirPressure;
                depletionPercentageObj.staticFluidLevel = 0;
            }
            else if (depletionPercentageObj.inflowType === 2) {
                depletionPercentageObj.staticFluidLevel = wellInput?.staticFluidLvlForHd;
            }

            return from(GetDepletionPercentage(depletionPercentageObj)).pipe(
                map(data => {
                    return setBasicWellIPRDepletionPercentage(data)
                }),
            )
        }),
    )
};

const getWellIPRFutureBHP = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/basicCalculation/setBasicWellIPRDepletionPressure",
            "jetlift/basicCalculation/setBasicWellIPRFutureProdRate",
            "jetlift/basicCalculation/setBasicIPRMethodologySelection"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.depletionPressure)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.futureProductionRate)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.IPRSelection)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.wellInflowData)),
        ),
        switchMap(([actionData, wellInput, fluidInput, depletionPressure, futureProductionRate, selectedIPR, wellInflowData]) => {

            if (wellInflowData && wellInflowData[0]) {
                let futureBHPObject = {} as FutureBHPInObject;
                futureBHPObject.inflowType = +(wellInput?.inflowTypeLstVal || 0);
                futureBHPObject.staticBHP = wellInput?.sbhpForPressre;
                futureBHPObject.futureShutInPressure = depletionPressure || 0;
                futureBHPObject.futureProductionRate = futureProductionRate || 0;
                futureBHPObject.maxFlowRate = +(wellInflowData[0]?.mxFlowRate4);
                futureBHPObject.combinedSpGr = fluidInput?.combinedSpGr;
                futureBHPObject.perforation = wellInput?.perforation;

                if (selectedIPR === 'ConstantPI') {
                    futureBHPObject.maxFlowRate = +(wellInflowData[0]?.mxFlowRate3);
                    futureBHPObject.selectedIPR = IPR.ConstantPI;
                    futureBHPObject.PI = +(wellInflowData[0]?.piStrVal3);
                } else if (selectedIPR === 'Composite') {
                    futureBHPObject.maxFlowRate = +(wellInflowData[0]?.mxFlowRate2);
                    futureBHPObject.selectedIPR = IPR.Composite;
                    futureBHPObject.PI = +(wellInflowData[0]?.piStrVal2);
                } else if (selectedIPR === 'Vogel') {
                    futureBHPObject.selectedIPR = IPR.Wiggins;
                } else if (selectedIPR === 'Wiggins') {
                    futureBHPObject.selectedIPR = IPR.Wiggins;
                }

                if (futureBHPObject.inflowType === 0) {
                    futureBHPObject.staticBHP = wellInput?.reservoirPressure;
                    futureBHPObject.staticBHHd = wellInput?.staticFluidLvl;
                }
                else if (futureBHPObject.inflowType === 2) {
                    futureBHPObject.staticBHHd = wellInput?.staticFluidLvlForHd;
                }

                return from(GetFutureBHP(futureBHPObject)).pipe(
                    map(data => {
                        return setBasicWellIPRFutureBHP(data)
                    }),
                )
            }
            return EMPTY;
        }),
    )
};

const getFutureProductionRate = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/basicCalculation/getFutureProductionRate"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.createProject?.jetliftProjectID)),
        ),
        switchMap(([actionData, projectId]) => {
            const projectIdValue = projectId?.projectId || "";
            return from(GetFutureProductionRate(projectIdValue)).pipe(
                map(data => {
                    return setBasicWellIPRFutureProdRate(data);
                })
            )
        })
    )
};

const getDepletionPressure = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/basicCalculation/getDepletionPressure"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.createProject?.jetliftProjectID)),
        ),
        switchMap(([actionData, fluidInput, wellInput, projectId]) => {
            const postObj = {
                inflowType: +(wellInput?.inflowTypeLstVal || 0),
                staticBHP: wellInput?.sbhpForPressre,
                staticFluidLevel: +wellInput?.staticFluidLvl || 0,
                finalCompositeSpGr: +fluidInput?.combinedSpGr || 0,
                projectID: projectId?.projectId || "",
            };
            return from(GetDepletionPressure(postObj)).pipe(
                map(data => {
                    return setBasicWellIPRDepletionPressure(data);
                })
            )
        })
    )
};

// need to add new epic related to depletion pressure and futur production then trigger IPR, depletion graphs
const getIPRPlotData = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/basicCalculation/setBasicWellIPRDepletionPercentage",
            "jetlift/gasCalculations/setGasCalculationComplete"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
            state$.pipe(map((state: RootState) => state?.jetlift?.gasCalculations?.GasCalculationComplete)),
        ),
        switchMap(([actionData, tubingInput, wellInput, fluidInput, unit, gasCalculationComplete]) => {

            if (gasCalculationComplete) {
                const bubblePoint = fluidInput?.bubblePoint;
                const bubblePresUnit = unit?.unitBubblePointListVal;
                const combinedspGr = fluidInput?.combinedSpGr;
                const inflowType = +(wellInput?.inflowTypeLstVal || 0);
                let flowRate = null;
                let flowingBHHd = null;
                let flowingBHPressure = null;
                let piValue = null;
                let staticBHHd = null;
                let staticBHPressure = null;
                const piUnit = unit?.unitPIValListVal;

                if (inflowType === 0) {
                    piValue = wellInput?.piVal?.toString();
                    staticBHPressure = wellInput?.reservoirPressure?.toString();
                    staticBHHd = wellInput?.staticFluidLvl?.toString();
                }
                if (inflowType === 1) {
                    flowRate = wellInput?.flowRate?.toString();
                    flowingBHPressure = wellInput?.intakePressure?.toString();
                    staticBHPressure = wellInput?.sbhpForPressre?.toString();
                }

                const flowingBHHdUnit = unit?.unitPrdcngFluidLvlListVal;
                const flowRateUnit = unit?.unitFlowRateListVal;
                const flowingBHPressureUnit = unit?.unitIntakePressureListVal;
                const staticBHHdUnit = unit?.unitStaticFluidLvlListVal;
                const staticBHPressureUnit = unit?.unitReservoirPressureListVal;
                const productionRate = wellInput?.productionRate;
                const productionRateUnit = unit?.unitProductionRateListVal;
                const waterCut = fluidInput?.waterCut;
                const unitSys = tubingInput?.unitSys;
                const perforation = wellInput?.perforation;
                const perforationUnit = unit?.unitPerforationListVal;
                const gasAvailability = fluidInput?.gasAvailblty;
                const isGasAvailable = gasAvailability && +gasAvailability ? true : false;

                const postObj = {
                    InflowType: inflowType,
                    StaticBHPressure: staticBHPressure,
                    StaticBHPressureUnit: staticBHPressureUnit,
                    PI: piValue,
                    PIUnit: piUnit,
                    BubblePtPressure: bubblePoint,
                    BubblePtPressureUnit: bubblePresUnit,
                    ProductionRate: productionRate,
                    ProductionRateUnit: productionRateUnit,
                    FlowingBHPressure: flowingBHPressure,
                    FlowingBHPressureUnit: flowingBHPressureUnit,
                    FlowRate: flowRate,
                    FlowRateUnit: flowRateUnit,
                    WaterCut: waterCut,
                    StaticBHHead: staticBHHd,
                    StaticBHHeadUnit: staticBHHdUnit,
                    FlowingBHHead: flowingBHHd,
                    FlowingBHHeadUnit: flowingBHHdUnit,
                    CombinedSpGr: combinedspGr,
                    Perforation: perforation,
                    PerforationUnit: perforationUnit,
                    UnitSystem: unitSys,
                    IsGas: isGasAvailable
                }

                return from(getIPRGraphData(postObj))
                    .pipe(
                        map(data => {
                            return setIPRChartData(data);
                        })
                    )
            }
            return EMPTY;
        }),
    )
};

const getDepletionPlotData = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/basicCalculation/setBasicWellIPRDepletionPercentage",
            "jetlift/gasCalculations/setGasCalculationComplete"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.gasCalculations?.GasCalculationComplete)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.depletionPercentage)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.wellInflowData)),
        ),
        switchMap(([actionData, tubingInput, wellInput, fluidInput, gasCalculationComplete, depletionPercent, wellInfo]) => {
            if (gasCalculationComplete) {
                const combinedspGr = fluidInput?.combinedSpGr;
                const inflowType = +(wellInput?.inflowTypeLstVal || 0);
                let flowRate = null;
                let flowingBHHd = null;
                let flowingBHPressure = null;
                let piValue = null;
                let staticBHHd = null;
                let staticBHPressure = null;

                if (inflowType === 0) {
                    piValue = wellInput?.piVal?.toString();
                    staticBHPressure = wellInput?.reservoirPressure?.toString();
                    staticBHHd = wellInput?.staticFluidLvl?.toString();
                }
                if (inflowType === 1) {
                    flowRate = wellInput?.flowRate?.toString();
                    flowingBHPressure = wellInput?.intakePressure?.toString();
                    staticBHPressure = wellInput?.sbhpForPressre?.toString();
                }
                if (inflowType === 2) {
                    flowRate = wellInput?.flowRateForHd?.toString();
                    flowingBHHd = wellInput?.prdcngFluidLvl;
                    staticBHHd = wellInput?.staticFluidLvlForHd?.toString();
                }

                const productionRate = wellInput?.productionRate;
                const waterCut = fluidInput?.waterCut;
                const unitSys = tubingInput?.unitSys;
                const perforation = wellInput?.perforation;

                const postObj = {
                    InflowType: inflowType,
                    StaticBHP: staticBHPressure,
                    PI: piValue,
                    FlowingBHPressure: flowingBHPressure,
                    ProductionRate: productionRate,
                    TestFlowRate: flowRate,
                    WaterCut: waterCut,
                    StaticBHHead: staticBHHd,
                    FlowBHHeadFlowBHHead: flowingBHHd,
                    CombinedSpGr: combinedspGr,
                    Perforation: perforation,
                    UnitSystem: unitSys,
                    DepletionPercent: depletionPercent,
                    VogelMaxFlowRate: +(wellInfo?.[0]?.mxFlowRate2),
                    CompositeMaxFlowRate: +(wellInfo?.[0]?.mxFlowRate2)
                }

                return from(getDepletionCurveData(postObj)).pipe(
                    mergeMap(data => {
                        return [
                            setDepletionChartData(data),
                            setBasicCalculationComplete(true),
                        ];
                    }),
                    catchError((error) => of(console.log(error))),
                    endWith(setLoading(false)),
                )
            };
            return EMPTY;
        }),
    )
};

const getBasicSummary = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/basicCalculation/setIPRChartData",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.jetPump?.jetPump)),
            state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.iprCurve)),
        ),
        switchMap(([actionData, tubingTab, wellInput, fluidInput, jetPump, iprCurve]) => {
            const maxLiquidRate = iprCurve?.vogel?.length > 0 ? iprCurve?.vogel?.[iprCurve?.vogel?.length - 1]?.liquidRate : 0;
            const payload = {
                wellheadPressure: +wellInput?.casingPressure || 0, // Pwh
                pumpIntakePressure: +wellInput?.desiredPumpSuctionPressure || 0, // Ps // pump suction pressure
                reservoirPressure: wellInput?.inflowTypeLstVal?.toString() === "0" ? +wellInput?.reservoirPressure : +wellInput?.sbhpForPressre || 0,            // Pr
                flowingBottomHolePressure: +wellInput?.intakePressure || 0,   // Pbh // flowing bottom hole pressure
                reservoirTemp: +fluidInput?.bottomHoleTemp || 0,              // Tr / ReservoirTemp Bottom hole temp
                productionRate: +wellInput?.productionRate || 0,             // Ql // Desired production rate
                reservoirRate: +wellInput?.flowRate || 0,                    // Qr // test flow rate
                powerFluidRate: 1467,                                       // Qn 1467
                trueVerticalDepth: +wellInput?.tvd || 0,                    // TVDP
                measuredDepth: +wellInput?.pumpSetting || 0,                // MDP
                powerFluidSpecificGravity: +jetPump?.powerFluidSpecificGravity || 0,// SGpf
                powerFluidType: jetPump?.powerFluidType?.toString() === "1" ? "Water" : "Oil",// PowerFluidType
                gasOilRatio: +fluidInput?.gor || 0,                          // GOR
                waterCut: +fluidInput?.waterCut || 0,                        // WC
                oilAPI: +fluidInput?.oilAPI || 0,                            // OilAPI
                gasSpecificGravity: +fluidInput?.spGrGas || 0,               // GasSG
                waterSpecificGravity: +fluidInput?.spGrWater || 0,           // WaterSG
                wellDeviationAngle: 90,                                      // AG (degrees) 90
                nozzleArea: +jetPump?.nozzleAreaSize || 0,                   // An
                throatArea: +jetPump?.throatAreaSize || 0,                   // At
                areaRatio: (+jetPump?.throatAreaSize && +jetPump?.nozzleAreaSize)
                    ? (+jetPump.nozzleAreaSize) / (+jetPump.throatAreaSize)
                    : 1,                                       // Ar
                nozzleDiameter: +jetPump?.nozzleDiameterSize || 0,           // nozzle diameter in JetPumpInput
                throatDiameter: +jetPump?.throatDiameterSize || 0,           // throat diameter in JetPumpInput
                nozzleCoefficient: 0.02,                                     // Kn 0.02
                throatDischargeCoefficient: 0.3,                             // Ktd 0.3
                tubingID: +tubingTab?.tubing2_tubingID || +tubingTab?.tubing1_tubingID || 0,   // D1
                tubingOD: +tubingTab?.tubing2_tubingOD || +tubingTab?.tubing1_tubingOD || 0,   // D2
                casingID: +tubingTab?.casingID || 0,                          // D3
                installationType: +jetPump?.tubingInstallationType || 0,      // JPInst = "Standard"
                surfaceInjectionPressure: +jetPump?.minFluidRange || 0,       // Calculated / optional
                maxLiquidRate: maxLiquidRate || 0,                            // vogel max liquid rate from ipr graph data
            } as basicSummaryPayload;
            return from(GetBasicSummary(payload)).pipe(
                map(data => {
                    return setBasicSummary(data);
                }),
                catchError((error) => of(console.log(error))),
            )
        })
    )
};

const getBasicSummaryScenarios = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/basicCalculation/loadbasicSummaryScenarios",
            "jetlift/gasCalculations/setGasCalculationComplete"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.jetPump?.jetPump)),
            state$.pipe(map((state: RootState) => state?.jetlift?.others?.othersInput)),
        ),
        switchMap(([actionData, tubingTab, wellInput, fluidInput, jetPump, othersInput]) => {
            const payload = {
                pwh: +wellInput?.casingPressure || 0, // wellhead pressure
                tr: +fluidInput?.bottomHoleTemp || 0, // reservoir temperature
                sgg: +fluidInput?.spGrGas || 0, // gas specific gravity
                gor: +fluidInput?.gor || 0, // gor
                api: +fluidInput?.oilAPI || 0, // oil api
                sgw: +fluidInput?.spGrWater || 0, // water specific gravity
                wc: +fluidInput?.waterCut || 0, // water cut
                qr: +wellInput?.reservoirPressure || 0, // reservoir pressure
                jpinst: jetPump?.tubingInstallationType?.toString() === "0" ? "Standard" : jetPump?.tubingInstallationType?.toString() === "1" ? "Reverse flow" : jetPump?.tubingInstallationType?.toString() === "2" ? "Parallel" : "Concentric", // jet pump installation type
                tvdp: +wellInput?.tvd || 0, // pump TVD
                mdp: +wellInput?.pumpSetting || 0, // pump MD
                d1: +tubingTab?.tubing1_tubingID || +tubingTab?.tubing2_tubingID || 0, // tubing ID
                d2: +tubingTab?.tubing1_tubingOD || +tubingTab?.tubing2_tubingOD || 0, // tubing OD
                d3: +tubingTab?.casingID || 0, // casing ID
                ql: +wellInput?.productionRate || 0, // flow rate desired
                ps: +wellInput?.desiredPumpSuctionPressure || 0, // pump suction pressure
                an: +jetPump?.nozzleAreaSize || 0, // nozzle area
                at: +jetPump?.throatAreaSize || 0, // throat area
                aa: 0.03281, // annulus area
                pft: jetPump?.powerFluidType?.toString() === "1" ? "Water" : "Oil", // power fluid type
                sgpf: +jetPump?.powerFluidSpecificGravity || 0, // power fluid specific gravity
                vispf: +fluidInput?.fluidViscosity || 0, // power fluid viscosity
                pt: jetPump?.outputPressures?.split(",")?.map((item: any) => Number(item.trim())),
                pso: 1500, // 1500
                ag: 90, // 90
                visg: 0.02, // 0.02
                d: +wellInput?.tvd || 0, // depth for pump discharge pressure
                ar: (+jetPump?.throatAreaSize && +jetPump?.nozzleAreaSize)
                    ? (+jetPump.nozzleAreaSize) / (+jetPump.throatAreaSize)
                    : 1, // area ratio (nozzle area / throat area), fallback 1 when invalid
                ktd: 0.3, // Throat diffuser coefficient
                kn: 0.02, // Nozzle coefficient
                StaticBHPressure: +wellInput?.sbhpForPressre || 0, // Static BH Pressure
                FlowingBHPressure: +wellInput?.intakePressure || 0, // Flowing BH Pressure
                TestFlowRate: +wellInput?.flowRate || 0, // Test Flow Rate
            } as basicSummaryScenariosPayload;
            return from(GetBasicSummaryScenarios(payload)).pipe(
                mergeMap(data => {
                    return [
                        setbasicSummaryScenarios(data?.resultSets),
                        setFormattedBasicSummaryScenarios(data?.resultSets),
                        setCavitationWarning(data?.cavitationWarning),
                    ];
                }),
                catchError((error) => of(console.log(error))),
            )
        })
    )
};

export const jetLiftBasicEpic = combineEpics(
    getWellInflowData,
    getWellIPRData,
    getWellIPRFutureBHP,
    getFutureProductionRate,
    getDepletionPressure,
    getIPRPlotData,
    getDepletionPlotData,
    getBasicSummary,
    getBasicSummaryScenarios,
);